import { getScrollContainer, getScrollTop, getScrollEventTarget, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

let disposeNavigation;

function initializeNavigation() {
  disposeNavigation?.();
  const header = document.querySelector('[data-gp-adaptive-header]');
  if (!header) return;
  const upper = header.querySelector('[data-gp-nav-upper]');
  const lower = header.querySelector('[data-gp-nav-lower]');
  const utilities = [...document.querySelectorAll('[data-gp-utility]')];
  const desktop = matchMedia('(min-width: 600px)');
  const root = document.documentElement;
  const pageWrapper = document.querySelector('.page-wrapper');
  const wideSpace = () => desktop.matches && root.dataset.gpNavNarrowSpace !== 'true';
  const syncAvailableWidth = () => {
    const squeezed = desktop.matches && pageWrapper?.classList.contains('page-wrapper--drawer-open');
    const width = squeezed ? pageWrapper.getBoundingClientRect().width : window.innerWidth;
    root.style.setProperty('--gp-nav-available-width', `${Math.max(0, Math.floor(width))}px`);
    root.dataset.gpNavDrawerSpace = String(Boolean(squeezed));
    root.dataset.gpNavNarrowSpace = String(Boolean(squeezed && width < 600));
    queueUpdate();
  };
  const controller = new AbortController();
  const { signal } = controller;
  let previousY = getScrollTop();
  let direction = 0;
  let distance = 0;
  let frame = 0;
  let scrollTarget;
  let footerObserver;
  let footerVisible = false;
  const stickyBar = document.querySelector('sticky-add-to-cart [ref="stickyBar"]');
  const syncContentOffset = () => {
    const visible = stickyBar?.dataset.stuck === 'true' && stickyBar.getClientRects().length > 0;
    const offset = visible ? Math.ceil(stickyBar.offsetHeight + (parseFloat(getComputedStyle(stickyBar).bottom) || 0)) : 0;
    root.style.setProperty('--gp-nav-content-offset', `${offset}px`);
  };
  const stickyObserver = new MutationObserver(syncContentOffset);
  const stickyResizeObserver = new ResizeObserver(syncContentOffset);
  if (stickyBar) {
    stickyObserver.observe(stickyBar, { attributes: true, attributeFilter: ['data-stuck'] });
    stickyResizeObserver.observe(stickyBar);
  }
  syncContentOffset();

  const closeLocalization = () => {
    utilities.forEach((utility) => {
      const details = utility.querySelector('details');
      if (details.open && details.contains(document.activeElement)) details.querySelector('summary').focus();
      details.open = false;
    });
  };

  const syncUtilities = () => {
    const compact = root.dataset.gpNavigation === 'compact';
    utilities.forEach((utility) => {
      const active = !footerVisible && (utility.dataset.gpUtility === 'top') === compact;
      utility.hidden = footerVisible;
      utility.inert = !active;
      utility.setAttribute('aria-hidden', String(!active));
    });
  };

  const setNavigation = (state) => {
    const compact = state === 'compact';
    if (root.dataset.gpNavigation !== state) closeLocalization();
    root.dataset.gpNavigation = state;
    upper.inert = compact;
    lower.inert = !compact;
    upper.setAttribute('aria-hidden', String(compact));
    lower.setAttribute('aria-hidden', String(!compact));
    syncUtilities();
    // Sticky product controls consume the visible header size, not its stable content spacer.
    const homeLift = wideSpace() && header.hasAttribute('data-gp-adaptive-home') && getScrollTop() <= 0 ? 112 : 0;
    document.body.style.setProperty('--header-height', `${compact ? 0 : Math.ceil(upper.offsetHeight + homeLift + 12)}px`);
  };

  const update = () => {
    frame = 0;
    syncContentOffset();
    const container = getScrollContainer();
    const maximum = Math.max(0, container.scrollHeight - container.clientHeight);
    const y = Math.max(0, Math.min(getScrollTop(), maximum));
    const delta = y - previousY;
    previousY = y;
    root.dataset.gpPagePosition = y <= 0 ? 'top' : 'scrolled';
    // A wide desktop viewport can leave a mobile-sized page beside the cart.
    // Show the existing compact rail at any scroll position in that space.
    if (root.dataset.gpNavNarrowSpace === 'true') {
      setNavigation('compact');
      distance = 0;
      direction = 0;
      return;
    }
    if (wideSpace() || y <= 24 || maximum - y <= 24) {
      setNavigation('expanded');
      distance = 0;
      direction = 0;
      return;
    }
    if (delta !== 0) {
      const nextDirection = Math.sign(delta);
      if (nextDirection !== direction) distance = 0;
      direction = nextDirection;
      distance += Math.abs(delta);
      if (direction > 0 && distance >= 100) setNavigation('compact');
      else if (direction < 0 && distance >= 50) setNavigation('expanded');
    }
  };
  const queueUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };

  const bindScroll = () => {
    scrollTarget?.removeEventListener('scroll', queueUpdate);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', queueUpdate, { passive: true, signal });
    previousY = getScrollTop();
    footerObserver?.disconnect();
    const footer = document.querySelector('.page-wrapper > footer');
    if (footer) {
      footerObserver = new IntersectionObserver(([entry]) => {
        footerVisible = entry.isIntersecting;
        if (footerVisible) closeLocalization();
        syncUtilities();
      }, { root: getIntersectionRoot() });
      footerObserver.observe(footer);
    }
    update();
  };

  utilities.forEach((utility) => {
    utility.querySelector('[data-gp-chat-open]').addEventListener('click', (event) => {
      const chat = document.querySelector('#chat-drawer shopify-chat');
      if (typeof chat?.show !== 'function') return; // The contact page remains available if Inbox is unavailable.
      event.preventDefault();
      closeLocalization();
      chat.show();
    }, { signal });
    utility.querySelector('details').addEventListener('keydown', (event) => {
      if (event.key === 'Escape') { event.preventDefault(); closeLocalization(); }
    }, { signal });
  });
  document.addEventListener('pointerdown', (event) => {
    if (!utilities.some((utility) => utility.contains(event.target))) closeLocalization();
  }, { signal });
  const widthObserver = new ResizeObserver(syncAvailableWidth);
  if (pageWrapper) widthObserver.observe(pageWrapper);
  const wrapperObserver = new MutationObserver(syncAvailableWidth);
  if (pageWrapper) wrapperObserver.observe(pageWrapper, { attributes: true, attributeFilter: ['class'] });
  window.addEventListener('resize', syncAvailableWidth, { signal });
  window.addEventListener('pageshow', bindScroll, { signal });
  desktop.addEventListener('change', () => { distance = 0; update(); }, { signal });
  scrollContainerMediaQuery.addEventListener('change', bindScroll, { signal });
  syncAvailableWidth();
  setNavigation('expanded');
  bindScroll();
  disposeNavigation = () => {
    controller.abort();
    scrollTarget?.removeEventListener('scroll', queueUpdate);
    footerObserver?.disconnect();
    stickyObserver.disconnect();
    stickyResizeObserver.disconnect();
    widthObserver.disconnect();
    wrapperObserver.disconnect();
    root.style.removeProperty('--gp-nav-available-width');
    delete root.dataset.gpNavDrawerSpace;
    delete root.dataset.gpNavNarrowSpace;
    cancelAnimationFrame(frame);
  };
}

initializeNavigation();
document.addEventListener('shopify:section:load', initializeNavigation);
document.addEventListener('shopify:section:unload', () => requestAnimationFrame(initializeNavigation));
